import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TabBtn = ({ active, children, onClick }) => (
  <button onClick={onClick} style={{
    padding: ".5rem 1rem", borderRadius: "8px 8px 0 0", border: "none", borderBottom: active ? "2px solid #EC4899" : "2px solid transparent",
    background: active ? "#fff" : "transparent", color: active ? "#EC4899" : "#6B7280", fontWeight: active ? 600 : 400,
    cursor: "pointer", fontSize: ".85rem", fontFamily: "Inter, sans-serif",
  }}>{children}</button>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: "1rem" }}>
    <label style={{ display: "block", fontSize: ".8rem", fontWeight: 600, color: "#374151", marginBottom: ".35rem" }}>{label}</label>
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder, type = "text" }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{ width: "100%", padding: ".55rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem", outline: "none", transition: "border .2s" }}
    onFocus={e => e.target.style.borderColor = "#EC4899"} onBlur={e => e.target.style.borderColor = "#D1D5DB"} />
);

export const AdminSettings = () => {
  const [tab, setTab] = useState("general");
  const [settings, setSettings] = useState({
    siteTitle: "", logoUrl: "", primaryColor: "#EC2E8B", secondaryColor: "#E6A24A",
    footerText: "", contactEmail: "", contactPhone: "",
    socialLinks: { facebook: "", instagram: "", linkedin: "", twitter: "" }
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/settings`).then(r => {
      if (r.data) setSettings(prev => ({ ...prev, ...r.data, socialLinks: { ...prev.socialLinks, ...(r.data.socialLinks || {}) } }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API}/api/admin/settings`, settings); toast.success("Settings saved!"); }
    catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const u = (key, val) => setSettings(p => ({ ...p, [key]: val }));
  const uSocial = (key, val) => setSettings(p => ({ ...p, socialLinks: { ...p.socialLinks, [key]: val } }));

  return (
    <div>
      <div style={{ display: "flex", borderBottom: "1px solid #E5E7EB", marginBottom: "1.5rem", gap: ".25rem" }}>
        {[
          { id: "general", label: "General" },
          { id: "branding", label: "Branding" },
          { id: "seo", label: "SEO" },
          { id: "integrations", label: "Integrations" },
          { id: "privacy", label: "Privacy" },
        ].map(t => <TabBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</TabBtn>)}
      </div>

      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", maxWidth: 700 }}>
        {tab === "general" && (<>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1.25rem" }}>General Settings</h3>
          <Field label="Site Title"><Input value={settings.siteTitle} onChange={e => u("siteTitle", e.target.value)} data-testid="settings-title-input" /></Field>
          <Field label="Logo URL"><Input value={settings.logoUrl} onChange={e => u("logoUrl", e.target.value)} data-testid="settings-logo-input" /></Field>
          {settings.logoUrl && <img src={settings.logoUrl} alt="Logo" style={{ maxHeight: 50, marginBottom: "1rem" }} />}
          <Field label="Footer Text"><Input value={settings.footerText} onChange={e => u("footerText", e.target.value)} /></Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <Field label="Contact Email"><Input value={settings.contactEmail} onChange={e => u("contactEmail", e.target.value)} /></Field>
            <Field label="Contact Phone"><Input value={settings.contactPhone} onChange={e => u("contactPhone", e.target.value)} /></Field>
          </div>
        </>)}

        {tab === "branding" && (<>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1.25rem" }}>Brand Colors</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <Field label="Primary Color">
              <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                <input type="color" value={settings.primaryColor} onChange={e => u("primaryColor", e.target.value)} style={{ width: 44, height: 36, border: "1px solid #D1D5DB", borderRadius: 6, cursor: "pointer" }} />
                <Input value={settings.primaryColor} onChange={e => u("primaryColor", e.target.value)} />
              </div>
            </Field>
            <Field label="Secondary Color">
              <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                <input type="color" value={settings.secondaryColor} onChange={e => u("secondaryColor", e.target.value)} style={{ width: 44, height: 36, border: "1px solid #D1D5DB", borderRadius: 6, cursor: "pointer" }} />
                <Input value={settings.secondaryColor} onChange={e => u("secondaryColor", e.target.value)} />
              </div>
            </Field>
          </div>
          <div style={{ marginTop: "1rem", padding: "1rem", borderRadius: 8, background: `linear-gradient(135deg, ${settings.primaryColor}, ${settings.secondaryColor})` }}>
            <p style={{ color: "#fff", margin: 0, fontWeight: 600, textAlign: "center" }}>Brand Gradient Preview</p>
          </div>
        </>)}

        {tab === "seo" && (<>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1.25rem" }}>SEO & Analytics</h3>
          <Field label="Site Description"><textarea rows={3} value={settings.footerText} onChange={e => u("footerText", e.target.value)} style={{ width: "100%", padding: ".55rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem", resize: "vertical" }} /></Field>
          <Field label="Google Analytics ID"><Input value="" placeholder="G-XXXXXXXXXX" onChange={() => {}} /></Field>
          <Field label="Facebook Pixel ID"><Input value="" placeholder="123456789" onChange={() => {}} /></Field>
        </>)}

        {tab === "integrations" && (<>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1.25rem" }}>Integrations</h3>
          <div style={{ padding: "1rem", background: "rgba(16,185,129,.05)", border: "1px solid rgba(16,185,129,.2)", borderRadius: 8, marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: ".6rem" }}>
            <i className="fas fa-check-circle" style={{ color: "#10B981" }} />
            <span style={{ fontSize: ".85rem", color: "#065F46" }}>Go High Level CRM connected and active</span>
          </div>
          <h4 style={{ fontSize: ".85rem", fontWeight: 700, color: "#374151", margin: "0 0 1rem" }}>Social Media Links</h4>
          <Field label="Facebook"><Input value={settings.socialLinks.facebook} onChange={e => uSocial("facebook", e.target.value)} placeholder="https://facebook.com/..." /></Field>
          <Field label="Instagram"><Input value={settings.socialLinks.instagram} onChange={e => uSocial("instagram", e.target.value)} placeholder="https://instagram.com/..." /></Field>
          <Field label="LinkedIn"><Input value={settings.socialLinks.linkedin} onChange={e => uSocial("linkedin", e.target.value)} placeholder="https://linkedin.com/..." /></Field>
          <Field label="Twitter / X"><Input value={settings.socialLinks.twitter} onChange={e => uSocial("twitter", e.target.value)} placeholder="https://twitter.com/..." /></Field>
        </>)}

        {tab === "privacy" && (<>
          <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1.25rem" }}>Privacy & Compliance</h3>
          <Field label="Privacy Policy URL"><Input value="" placeholder="https://yoursite.com/privacy" onChange={() => {}} /></Field>
          <Field label="Terms of Service URL"><Input value="" placeholder="https://yoursite.com/terms" onChange={() => {}} /></Field>
          <div style={{ display: "flex", alignItems: "center", gap: ".6rem", marginTop: ".5rem" }}>
            <input type="checkbox" id="gdpr" style={{ accentColor: "#EC4899" }} />
            <label htmlFor="gdpr" style={{ fontSize: ".85rem", color: "#374151" }}>Enable GDPR compliance mode</label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".6rem", marginTop: ".6rem" }}>
            <input type="checkbox" id="cookie" style={{ accentColor: "#EC4899" }} />
            <label htmlFor="cookie" style={{ fontSize: ".85rem", color: "#374151" }}>Show cookie consent banner</label>
          </div>
        </>)}

        <div style={{ marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px solid #E5E7EB" }}>
          <button onClick={save} disabled={saving} data-testid="settings-save-button" style={{
            padding: ".6rem 1.5rem", borderRadius: 8, border: "none",
            background: "linear-gradient(135deg, #EC4899, #F97316)", color: "#fff", cursor: "pointer", fontSize: ".85rem", fontWeight: 600,
          }}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
};
